import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase.ts';
import { formatCurrency, formatDate, formatMonthYear, formatPhoneNumber, getOrdinalSuffix } from '../utils/formatters.ts';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import '../styles/PaymentDetails.css';
import '../styles/BillDetails.css'; // Import for table styles

const PaymentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<any>(null);
  const [relatedPayments, setRelatedPayments] = useState<any[]>([]);
  const [tenantBills, setTenantBills] = useState<any[]>([]);

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select(`
          *,
          tenant:tenants (
            id,
            full_name,
            phone,
            email,
            monthly_rent,
            payment_due_date
          ),
          property:properties (
            id,
            name,
            address,
            property_type,
            planned_rent
          ),
          bill:bills (
            id,
            month,
            amount,
            paid_amount,
            status,
            due_date,
            payment_date
          )
        `)
        .eq('id', id)
        .single();

      if (paymentError) throw paymentError;
      setPayment(paymentData);

      // Fetch related payments regardless of bill status
      if (paymentData?.bill_id) {
        const { data: relatedPaymentsData } = await supabase
          .from('payments')
          .select('*')
          .eq('bill_id', paymentData.bill_id)
          .order('payment_date', { ascending: false });
        
        setRelatedPayments(relatedPaymentsData || []);
      }

      // Fetch tenant bills
      if (paymentData?.tenant_id) {
        const { data: bills } = await supabase
          .from('bills')
          .select('*')
          .eq('tenant_id', paymentData.tenant_id)
          .order('month', { ascending: false });
        
        setTenantBills(bills || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]); // Add id as dependency

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]); // Use fetchPaymentDetails as dependency

  const handlePaymentClick = (paymentId: string) => {
    if (paymentId !== id) { // Don't navigate if clicking current payment
      navigate(`/payments/${paymentId}`);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!payment) return <ErrorMessage message="Payment not found" />;


  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'status-paid';
      case 'unpaid': return 'status-unpaid';
      case 'partial': return 'status-partial';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  return (
    <div className="payment-details-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/payments')}>
          <span className="back-icon">←</span>
          <span>Back to Payments</span>
        </button>
        <h1>Payment Details</h1>
      </div>

      <div className="bill-details-page"> {/* Using the same container class */}
        {/* Left side - History sections */}
        <div className="history-section">
          {/* Payment History */}
          <div className="history-container">
            <h2>Payment History</h2>
            <div className="payment-history">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Mode</th>
                    <th>Paid To</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedPayments.map(relatedPayment => (
                    <tr 
                      key={relatedPayment.id}
                      className={relatedPayment.id === payment.id ? 'current-payment' : ''}
                      onClick={() => handlePaymentClick(relatedPayment.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{formatDate(relatedPayment.payment_date)}</td>
                      <td>{formatCurrency(relatedPayment.total_amount)}</td>
                      <td>{relatedPayment.payment_mode}</td>
                      <td>{relatedPayment.payment_to}</td>
                      <td>{relatedPayment.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bill History */}
          <div className="history-container">
            <h2>Tenant's Bill History</h2>
            <div className="bills-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Paid Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantBills.map(bill => (
                    <tr 
                      key={bill.id}
                      className={bill.id === payment?.bill_id ? 'current-bill' : ''}
                      onClick={() => navigate(`/bills/${bill.id}`)}
                    >
                      <td>{formatMonthYear(bill.month)}</td>
                      <td>{formatCurrency(bill.amount)}</td>
                      <td>{formatDate(bill.due_date)}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(bill.status)}`}>
                          <span className="status-dot"></span>
                          {bill.status}
                        </span>
                      </td>
                      <td>{formatCurrency(bill.paid_amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right side - Info cards */}
        <div className="info-section">
          {/* Payment Information */}
          <div className="detail-card">
            <h2>Payment Information</h2>
            <div className="detail-content">
              <p><strong>Amount:</strong> {formatCurrency(payment.total_amount)}</p>
              <p><strong>Date:</strong> {formatDate(payment.payment_date)}</p>
              <p><strong>Mode:</strong> {payment.payment_mode}</p>
              <p><strong>Paid To:</strong> {payment.payment_to}</p>
              {payment.settlement_payment && (
                <p><strong>Type:</strong> <span className="settlement-badge">Settlement Payment</span></p>
              )}
              {payment.notes && (
                <p><strong>Notes:</strong> {payment.notes}</p>
              )}
            </div>
          </div>

          {/* Bill Details */}
          {payment.bill && (
            <div className="detail-card">
              <h2>Bill Details</h2>
              <div className="detail-content">
                <p><strong>Month:</strong> {formatDate(payment.bill.month)}</p>
                <p><strong>Total Amount:</strong> {formatCurrency(payment.bill.amount)}</p>
                <p><strong>Paid Amount:</strong> {formatCurrency(payment.bill.paid_amount)}</p>
                <p><strong>Due Date:</strong> {formatDate(payment.bill.due_date)}</p>
                <p>
                  <strong>Status:</strong> 
                  <span className={`status-badge ${getStatusClass(payment.bill.status)}`}>
                    <span className="status-dot"></span>
                    {payment.bill.status}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Tenant Information */}
          <div className="detail-card">
            <h2>Tenant Information</h2>
            <div className="detail-content">
              <p><strong>Name:</strong> {payment.tenant.full_name}</p>
              <p><strong>Phone:</strong> {formatPhoneNumber(payment.tenant.phone)}</p>
              <p><strong>Email:</strong> {payment.tenant.email}</p>
              <p><strong>Monthly Rent:</strong> {formatCurrency(payment.tenant.monthly_rent)}</p>
              <p><strong>Payment Due Date:</strong> {getOrdinalSuffix(payment.tenant.payment_due_date)} of every month</p>
            </div>
          </div>

          {/* Property Information */}
          <div className="detail-card">
            <h2>Property Information</h2>
            <div className="detail-content">
              <p><strong>Name:</strong> {payment.property.name}</p>
              <p><strong>Address:</strong> {payment.property.address}</p>
              <p><strong>Type:</strong> {payment.property.property_type}</p>
              <p><strong>Planned Rent:</strong> {formatCurrency(payment.property.planned_rent)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsPage;
